import moment from 'moment';
import each from 'lodash/each';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import unescape from 'lodash/unescape';
import find from 'lodash/find';
import clone from 'lodash/clone';
import isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';

export default class Calendar {

  static momentFromFilter(filter) {
    const formattedFilter = Object.assign(Calendar.filterFromMoment(moment()), filter);
    const { month, day, year } = formattedFilter;
    return moment(`${month}-${day}-${year}`, 'MM-DD-YYYY');
  }

  static filterFromMoment(today) {
    return { day: today.format('D'), month: today.format('M'), year: today.format('YYYY') };
  }

  static determineIdPrefix(subsection) {
    switch (subsection.replace('_restricted', '')) {
    case 'school_local_facility':
    case 'school_group_school':
    case 'school_equipment':
    case 'district_school':
      return 'ds_';
    case 'school_remote_facility':
    case 'community_group_school':
    case 'non_district_school':
      return 'nds_';
    case 'directory_school':
      return 'drs_';
    case 'local_facility':
      return 'fl_';
    case 'remote_facility':
      return 'fr_';
    case 'google_place':
      return 'ggpl_';
    case 'school_group':
      return 'gps_';
    case 'community_group':
      return 'gpc_';
    case 'equipment':
      return 'eq_';
    case 'department':
      return 'dp_';
    case 'activity':
      return 'at_';
    case 'event_type':
      return 'et_';
    case 'user':
      return 'us_';
    case 'contact':
      return 'ct_';
    default:
      return '';
    }
  }

  /**
   * Get an array weeks each contain an array of days
   * @param  startDate  Format "2015-10-18"
   * @param  endDate    Format "2015-10-24"
   * @throws error if startDate is not a Sunday
   * @return {Array}
   */
  static getMatrix(startDate, endDate) {
    const start = moment(startDate, 'YYYY-MM-DD');
    const end = moment(endDate, 'YYYY-MM-DD');
    const daysDiff = end.diff(start, 'days');
    const today = moment().format('YYYY-MM-DD');

    const dayOfWeekStart = Calendar.getDayOfWeekStart();

    const start_day_of_week = start.format('dd');

    if ((dayOfWeekStart !== 2 && start_day_of_week !== 'Su') || (dayOfWeekStart === 2 && start_day_of_week !== 'Mo')) {
      // console.error(`Expected start date to be a Sunday. It was a ${start.format('dddd')}`);
      return [[]];
    }

    const output = [];
    let week = [];
    for (let i = 0; i <= daysDiff; i++) {
      const current = moment(start).add(i, 'days');
      week.push(Calendar.getMatrixDay(current, today));

      if ((i + 1) % 7 === 0) {
        output.push(week);
        week = [];
      }
    }
    if (week.length) {
      output.push(week);
    }

    return output;
  }

  static getMatrixDay(day, today) {
    const currentFormattedDate = day.format('YYYY-MM-DD');

    return {
      date: currentFormattedDate,
      dayOfWeek: day.format('ddd'),
      isToday: currentFormattedDate === today,
      moment: moment(day),
    };
  }

  static getWeekMatrix(week, year) {
    const today = moment().format('YYYY-MM-DD');

    const thisWeek = moment(`${week} ${year}`, 'w YYYY');
    const result = [];

    const dayOfWeekStart = Calendar.getDayOfWeekStart();

    if(dayOfWeekStart === 2) {
      var start_day_of_week = 1;
      var end_day_of_week = 8;
    } else {
      var start_day_of_week = 0;
      var end_day_of_week = 7;
    }

    for (let i = start_day_of_week; i < end_day_of_week; i++) {
      const current = thisWeek.day(i);
      result.push(Calendar.getMatrixDay(current, today));
    }

    return result;
  }

  static getMonthMatrix(month, year) {
    const result = [];
    const weeksInYear = moment().set('year', year).weeksInYear();
    let week = moment()
      .set('year', year)
      .set('month', month)
      .set('date', 1)
      .week();
    let yearTemp = year;

    for (let i = 0; i < 6; i++) {
      if (week + i > weeksInYear) {
        yearTemp += 1;
        week = 1 - i;
      }
      result.push(Calendar.getWeekMatrix(week + i, yearTemp));
    }

    return result;
  }

  static militaryToAmPm(time, opts = {}) {
    if (!isString(time)) {
      return '';
    }

    const parts = time.split(':');
    if (!parts.length) {
      return '';
    }

    let SPACE = ' ';
    if (opts.space !== undefined) {
      SPACE = opts.space ? SPACE : '';
    }

    let AM = 'am';
    let PM = 'pm';
    if (opts.caps !== undefined) {
      AM = opts.caps ? 'AM' : 'am';
      PM = opts.caps ? 'PM' : 'pm';
    }

    let hour = parseInt(parts[0], 10);
    if (hour > 12) {
      hour -= 12;
      return `${hour}:${parts[1]}${SPACE}${PM}`;
    }
    return `${hour}:${parts[1]}${SPACE}${AM}`;
  }

  // Take an API generated event object and do standard maniplations to it useful for rendering
  // @TODO do an optimization pass over this
  static massageEvent(originalEvent, originalMetadata) {
    const event = originalEvent;
    const metadata = originalMetadata || {};

    if (!event || !event.id) {
      return { isError: true, isLoading: false };
    }

    if (event._isProcessed) {
      return event;
    }

    const timeFormat = Calendar.getTimeMomentFormat();

    let type = null;
    let status = null;

    if (isObject(event.type)) {
      type = {
        id: parseInt(event.type.id, 10),
        name: unescape(event.type.data),
      };
    } else {
      type = {
        id: parseInt(event.type, 10),
      };
    }

    event.type = type;

    if (isObject(event.status)) {
      status = {
        id: parseInt(event.status.id, 10),
        name: unescape(event.status.data),
      };
    } else {
      status = {
        id: parseInt(event.status, 10),
      };
    }

    event.status = status;

    let timeDismiss = null;
    let showAdvantage = false;

    const earlyDismissal = {
      schools: {},
      groups: {},
    };
    const schools = [];
    const groups = [];
    const user_participants = [];
    const locations = [];
    const participants = [];
    const equipment = [];
    const tags = [];
    const contacts = [];
    const supervisors = [];

    each(event.time_dismiss, (timeDismissPart) => {
      if (isObject(timeDismissPart.school_id)) {
        earlyDismissal.schools[timeDismissPart.school_id.id] = timeDismissPart.time_dismiss;

        if (timeDismiss === null || timeDismiss === timeDismissPart.time_dismiss) {
          timeDismiss = timeDismissPart.time_dismiss;
        } else {
          timeDismiss = null;
        }
      }
    });

    if (timeDismiss !== null) {
      timeDismiss = moment(timeDismiss, 'HH:mm:ss').format(timeFormat);
    }

    event.time_dismiss = timeDismiss;

    each(event.home_away, (participant) => {
      const insert = {
        id: -1,
        home: participant.home === 't',
        away: participant.away === 't',
        neutral: participant.neutral === 't',
        tag: participant.tag === 't',
        time_dismiss: '',
      };

      // Apply a text label if the group/school is home, away or neutral
      insert.advantage = 'neutral';
      if (insert.home || insert.away) {
        insert.advantage = insert.home ? 'home' : 'away';
        showAdvantage = true;
      }

      if (isObject(participant.group_id)) {
        insert.id = participant.group_id.id;
        insert.name = unescape(participant.group_id.name);
        groups.push(insert);

        insert.type = 'group';

        participants.push(insert);
      }

      if (isObject(participant.user_account_id)) {
        insert.id = participant.user_account_id.id;
        insert.name = unescape(`${participant.user_account_id.first_name} ${participant.user_account_id.last_name}`),
        insert.email = participant.user_account_id.email,
        user_participants.push(insert);

        insert.type = 'user';

        participants.push(insert);
      }

      if (isObject(participant.school_id)) {
        if (participant.tag === 't') {
          const tag = {
            id: participant.school_id.id,
            type: 'school',
            name: unescape(participant.school_id.name),
            advantage: insert.advantage,
            time_dismiss: '',
          };

          if (isObject(earlyDismissal.schools[participant.school_id.id])) {
            tag.time_dismiss = earlyDismissal.schools[participant.school_id.id];
          }
          tags.push(tag);
        } else {
          insert.id = participant.school_id.id;
          insert.name = unescape(participant.school_id.name);
          if (isObject(earlyDismissal.schools[participant.school_id.id])) {
            insert.time_dismiss = earlyDismissal.schools[participant.school_id.id];
          }
          schools.push(insert);

          insert.type = 'school';

          participants.push(insert);
        }
      }
    });

    event.schools = schools;
    event.groups = groups;
    event.user_participants = user_participants;
    event.participants = participants;

    event.show_advantage = showAdvantage;

    each(event.locations, (location) => {
      const insert = {
        id: -1,
        type: '',
        name: 'UNKNOWN',
      };

      if (isObject(location.facility_id)) {
        insert.type = 'facility';
        insert.id = location.facility_id.id;
        insert.name = unescape(location.facility_id.name);
        insert.address = {
          address: location.facility_id.address || null,
          city: location.facility_id.city || null,
          state: location.facility_id.state || null,
          zip: location.facility_id.zip || null,
          ggpl_id: location.facility_id.ggpl_id || null,
        };
        insert.school = {};
        if (location.facility_id.school) {
          insert.school = { ...location.facility_id.school[0] };
        }
        insert.parents = [];
        if (location.facility_id.parents) {
          location.facility_id.parents.forEach((parent) => {
            insert.parents.push({ ...parent.parent_id });
          });
        }
        locations.push(insert);
      }

      if (isObject(location.school_id)) {
        insert.type = 'school';
        insert.id = location.school_id.id;
        insert.name = unescape(location.school_id.name);
        insert.address = {
          address: location.school_id.address || null,
          city: location.school_id.city || null,
          state: location.school_id.state || null,
          zip: location.school_id.zip || null,
          ggpl_id: location.school_id.ggpl_id || null,
        };
        locations.push(insert);
      }
    });
    event.locations = locations;

    each(event.equipment, (equipmentPart) => {
      const insert = {
        id: 0,
        name: '',
        total_quantity: 0,
        used_quantity: 0,
      };

      if (isObject(equipmentPart.equipment_id)) {
        insert.id = equipmentPart.equipment_id.id;
        insert.name = equipmentPart.equipment_id.name;
        if (equipmentPart.equipment_id.quantity) {
          insert.total_quantity = equipmentPart.equipment_id.quantity;
        }
      }

      if (equipmentPart.quantity_used) {
        insert.used_quantity = equipmentPart.quantity_used;
      }

      // Support html entity encoded name
      insert.name = unescape(insert.name);
      equipment.push(insert);
    });
    event.equipment = equipment;

    each(event.departments, (department) => {
      const insert = {
        id: department.id,
        type: 'department',
        name: unescape(department.name),
        advantage: '',
        time_dismiss: '',
      };

      tags.push(insert);
    });

    each(event.activities, (activity) => {
      const insert = {
        id: activity.id,
        type: 'activity',
        name: unescape(activity.name),
        advantage: '',
        time_dismiss: '',
      };

      tags.push(insert);
    });

    if (isObject(event.event_type)) {
      const insert = {
        id: event.event_type.id,
        type: 'event_type',
        name: unescape(event.event_type.name),
        advantage: '',
        time_dismiss: '',
      };

      tags.push(insert);
    }

    event.tags = tags;

    each(event.contacts, (contact) => {
      const insert = {
        id: contact.id,
        name: unescape(`${contact.first_name} ${contact.last_name}`),
        email: contact.email,
        work_phone: contact.work_phone,
      };

      contacts.push(insert);
    });
    event.contacts = contacts;

    each(event.supervisors, (supervisor) => {
      const insert = {
        id: supervisor.id,
        name: unescape(`${supervisor.first_name} ${supervisor.last_name}`),
        email: supervisor.email,
        work_phone: supervisor.work_phone,
      };

      supervisors.push(insert);
    });
    event.supervisors = supervisors;

    let numAttending = '';

    if (event.num_adults > 0 || event.num_students > 0 || event.num_wheelchairs > 0) {
      if (event.type.id === 5) {
        if (event.num_adults > 0) {
          numAttending += `${event.num_adults}`;
        }
      } else {
        if (event.num_adults > 0) {
          numAttending += `${event.num_adults} Adult`;
          if (event.num_adults > 1) numAttending += 's';
          if (event.num_students > 0 || event.num_wheelchairs > 0) numAttending += ' - ';
        }
        if (event.num_students > 0) {
          numAttending += `${event.num_students} Student`;
          if (event.num_students > 1) numAttending += 's';
          if (event.num_wheelchairs > 0) numAttending += ' - ';
        }
        if (event.num_wheelchairs > 0) {
          if (event.num_wheelchairs > 1) numAttending += ` ${event.num_wheelchairs} People with Wheelchairs`;
          else numAttending += ` ${event.num_wheelchairs} Person with a Wheelchair`;
        }
      }
    }

    event.num_attending = numAttending;

    if (event.type.id === 5) {
      if (metadata.total_cost) {
        event.total_cost = metadata.total_cost;
      }

      const lineItemsByFacilityId = {};

      if (event.line_items && event.line_items.length > 0) {
        each(event.line_items, (lineItem) => {
          if (lineItem.required === 't' || lineItem.selected === 't') {
            if (!lineItemsByFacilityId[lineItem.facility_id.id]) {
              lineItemsByFacilityId[lineItem.facility_id.id] = {};

              lineItemsByFacilityId[lineItem.facility_id.id].id = lineItem.facility_id.id;
              lineItemsByFacilityId[lineItem.facility_id.id].name = lineItem.facility_id.name;
              lineItemsByFacilityId[lineItem.facility_id.id].lineItems = [];
            }

            const payRateType = lineItem.line_item_id.pay_rate.type.id;
            const payRateNormalPay = lineItem.line_item_id.pay_rate.normal_pay;

            let costOut;

            if (payRateType === 7 || payRateType === 8 || payRateType === 9 || payRateType === 10) {
              const range = find(lineItem.line_item_id.pay_rate.ranges, r =>
                (lineItem.quantity >= r.range_start && lineItem.quantity <= r.range_end)
              );

              if (range) {
                switch (payRateType) {
                case 7:
                  costOut = `$${range.rate} / attendee`;
                  break;

                case 8:
                  costOut = `${range.range_start} to ${range.range_end} attendees`;
                  break;

                case 9:
                  costOut = `$${range.rate} / hour`;
                  break;

                case 10:
                  costOut = `${range.range_start} to ${range.range_end} minutes`;
                  break;

                default:
                  costOut = 0.0;
                }
              }
            } else if(payRateType === 11 || payRateType === 12) {

              const dayOfWeek = moment(event.date_start, 'E');
              const dayOfWeekName = moment(event.date_start, 'dddd');

              const range = find(lineItem.line_item_id.pay_rate.ranges, r =>
                (dayOfWeek == r.range_start)
              );

              if (range) {
                switch (payRateType) {
                case 11:
                  costOut = `${dayOfWeekName}: $${range.rate}`;
                  break;

                case 12:
                  costOut = `${dayOfWeekName}: $${range.rate} / hour`;
                  break;

                default:
                  costOut = 0.0;
                }
              }

            } else if(payRateType === 13) {

              if (has(window.CONFIG, 'facility_request_intand_fee')) var facility_request_intand_fee = parseFloat(window.CONFIG.facility_request_intand_fee);
              else var facility_request_intand_fee = 0.6;

              if (has(window.CONFIG, 'connect_stripe_card_percent')) var connect_stripe_card_percent = parseFloat(window.CONFIG.connect_stripe_card_percent);
              else var connect_stripe_card_percent = 2.9;

              if (has(window.CONFIG, 'connect_stripe_card_flat_fee')) var connect_stripe_card_flat_fee = parseFloat(window.CONFIG.connect_stripe_card_flat_fee);
              else var connect_stripe_card_flat_fee = 0.30;

              var facility_request_processing_fee = facility_request_intand_fee + connect_stripe_card_percent;

              var costOut = '$' + connect_stripe_card_flat_fee + ' + ' + facility_request_processing_fee + '%';
            } else {
              switch (payRateType) {
              case 3:
                costOut = `$${payRateNormalPay}`;
                break;

              case 4:
                costOut = `$${payRateNormalPay} / hour`;
                break;

              case 5:
                costOut = `$${payRateNormalPay}%`;
                break;

              case 6:
                costOut = `$${payRateNormalPay} / person`;
                break;

              default:
                costOut = payRateNormalPay;
              }
            }

            let quantityOutput = '';

            if (payRateType === 4 || payRateType === 9 || payRateType === 10 || payRateType === 12) {
              let quantityMinutes = 0;
              let quantityHours = 0;

              if (lineItem.quantity > 0) {
                quantityMinutes = lineItem.quantity % 60;
                quantityHours = Math.floor(lineItem.quantity / 60);
              } else {
                quantityMinutes = 0;
                quantityHours = 0;
              }

              quantityOutput = `${quantityHours} hrs ${quantityMinutes} mins`;
            } else if (payRateType === 5 || payRateType === 11 || payRateType === 13) {
              quantityOutput = 'N/A';
            } else {
              quantityOutput = lineItem.quantity;
            }

            const insert = {
              id: lineItem.line_item_id.id,
              name: lineItem.line_item_id.name,
              cost: costOut,
              quantity: quantityOutput,
              amount: lineItem.amount,
            };

            lineItemsByFacilityId[lineItem.facility_id.id].lineItems.push(insert);
          }
        });
      }

      event.line_items_by_facility_id = lineItemsByFacilityId;

      const formsByFacilityId = {};

      if (event.forms && event.forms.length > 0) {
        each(event.forms, (form) => {
          if (form.required === 't' || form.filled_out === 't') {
            if (!formsByFacilityId[form.facility_id.id]) {
              formsByFacilityId[form.facility_id.id] = {};

              formsByFacilityId[form.facility_id.id].id = form.facility_id.id;
              formsByFacilityId[form.facility_id.id].name = form.facility_id.name;
              formsByFacilityId[form.facility_id.id].forms = [];
            }

            let insert = {
              id: form.form_id.id,
              name: form.form_id.name,
              filled_out: form.filled_out,
              attachment_id: null,
            };

            if (form.form_id.attachment_id !== null) {
              insert.attachment_id = form.form_id.attachment_id.id;
            }

            formsByFacilityId[form.facility_id.id].forms.push(insert);
          }
        });
      }

      event.forms_by_facility_id = formsByFacilityId;
    }

    if (event.transportation_requests && event.transportation_requests.length > 0) {
      const transportationRequests = [];

      each(event.transportation_requests, (request) => {
        let requestStatus;
        let requestStatusName;

        if (isObject(request.status)) {
          requestStatus = request.status.id;
          requestStatusName = request.status.data;
        } else {
          requestStatus = request.status;
          requestStatusName = '';
        }

        let requestAlert;
        let requestAlertName;

        if (isObject(request.alert)) {
          requestAlert = request.alert.id;
          requestAlertName = request.alert.data;
        } else {
          requestAlert = request.alert;
          requestAlertName = '';
        }

        const insert = {
          id: request.id,
          school_id: parseInt(request.school_id.id, 10),
          school_name: unescape(request.school_id.name),
          school_depart: moment(request.school_depart),
          dest_arrival: moment(request.dest_arrival),
          dest_depart: moment(request.dest_depart),
          return_arrival: moment(request.return_arrival),
          num_adults: parseInt(request.num_adults, 10),
          num_students: parseInt(request.num_students, 10),
          num_wheelchairs: parseInt(request.num_wheelchairs, 10),
          num_compartments: parseInt(request.num_compartments, 10),
          status: parseInt(requestStatus, 10),
          status_name: unescape(requestStatusName),
          alert: parseInt(requestAlert, 10),
          alert_name: unescape(requestAlertName),
          removed: request.removed,
          notes: unescape(request.notes),
        };

        transportationRequests.push(insert);
      });

      event.transportation_requests = transportationRequests;
    }

    if (event.trips && event.trips.length > 0) {
      const trips = [];

      each(event.trips, (trip) => {
        let tripType;
        let tripTypeName;

        if (isObject(trip.trip_type)) {
          tripType = trip.trip_type.id;
          tripTypeName = trip.trip_type.data;
        } else {
          tripType = trip.trip_type;
          tripTypeName = '';
        }

        let tripDriver;
        let tripBus;
        let tripCharter;

        if (isObject(trip.driver)) {
          tripDriver = trip.driver;
        } else {
          tripDriver = null;
        }

        if (isObject(trip.bus)) {
          tripBus = trip.bus;
        } else {
          tripBus = null;
        }

        if (isObject(trip.charter)) {
          tripCharter = trip.charter;
        } else {
          tripCharter = null;
        }

        const goingPickup = [];
        const goingDropoff = [];
        const returnPickup = [];
        const returnDropoff = [];

        if (trip.going_pickup && trip.going_pickup.length > 0) {
          each(trip.going_pickup, (tripLoc) => {
            goingPickup.push(tripLoc.name);
          });
        }

        if (trip.going_dropoff && trip.going_dropoff.length > 0) {
          each(trip.going_dropoff, (tripLoc) => {
            goingDropoff.push(tripLoc.name);
          });
        }

        if (trip.return_pickup && trip.return_pickup.length > 0) {
          each(trip.return_pickup, (tripLoc) => {
            returnPickup.push(tripLoc.name);
          });
        }

        if (trip.return_dropoff && trip.return_dropoff.length > 0) {
          each(trip.return_dropoff, (tripLoc) => {
            returnDropoff.push(tripLoc.name);
          });
        }

        const insert = {
          id: trip.id,
          number: trip.number,
          driver: tripDriver,
          bus: tripBus,
          charter: tripCharter,
          pre_trip_start: moment(trip.pre_trip_start),
          going_depart: moment(trip.going_depart),
          going_arrival: moment(trip.going_arrival),
          return_depart: moment(trip.return_depart),
          return_arrival: moment(trip.return_arrival),
          going_pickup: goingPickup,
          going_dropoff: goingDropoff,
          return_pickup: returnPickup,
          return_dropoff: returnDropoff,
          type: parseInt(tripType, 10),
          type_name: unescape(tripTypeName),
        };

        trips.push(insert);
      });

      event.trips = trips;
    }

    let recurrenceDescShort = '';
    let recurrenceDescLong = '';

    if (isObject(event.recurrence_id)) {
      recurrenceDescShort = `Recurs ${event.recurrence_id.desc1}`;
      if (event.recurrence_id.desc2) recurrenceDescLong = event.recurrence_id.desc2;
    }

    event.recurrence_desc_short = recurrenceDescShort;
    event.recurrence_desc_long = recurrenceDescLong;

    event.do_not_show_edit = false;

    if (event.changes && event.changes.length > 0) {
      each(event.changes, (change, key) => {
        const actions = change.change_actions;
        if (actions && actions.length === 2 && actions[0].text === 'Approve' && actions[1].text === 'Decline') {
          actions.push(clone(actions[1]));
          actions[1] = {
            display_type: '',
            text: 'Edit',
            href: 'EDIT_EVENT',
          };

          event.do_not_show_edit = true;
        }
        event.changes[key].change_actions = actions;
      });
    }

    event.has_setup_breakdown = !isEmpty(event.time_setup) || !isEmpty(event.time_breakdown);

    // Support html entity encoded fields
    event.name = unescape(event.name);
    event.description = unescape(event.description);
    event.setup_instructions = unescape(event.setup_instructions);
    event.notes = unescape(event.notes);

    // Establish default labels
    event.labels = {
      start_time: 'All Day',
      end_time: '',
      time_duration: 'All Day',
      location: '',
      start_moment: moment(`${event.date_start} ${(event.time_start || '00:00 am')}`, 'YYYY-MM-DD ' + timeFormat),
      end_moment: moment(`${(event.date_end || event.date_start)} ${(event.time_end || '11:59 pm')}`, 'YYYY-MM-DD ' + timeFormat),
    };

    if (event.date) {
      event.labels.YYYMMDD = moment.unix(event.date).format('YYYY-MM-DD');
    } else {
      event.labels.YYYMMDD = event.labels.start_moment.format('YYYY-MM-DD');
    }
    event.all_day = true;
    event.overnight = false;

    // Check for all day
    if (event.time_start) {
      event.labels.start_time = event.time_start;
      if (event.tbd_start === 't') {
        event.labels.start_time = 'TBD';
      } else if (event.labels.start_time === '12:00 pm' || event.labels.start_time === '12:00') {
        event.labels.start_time = 'Noon';
      } else if (event.labels.start_time === '12:00 am' || event.labels.start_time === '00:00') {
        event.labels.start_time = 'Midnight';
      }

      if (event.time_end) {
        event.labels.end_time = event.time_end;
        if (event.tbd_end === 't') {
          event.labels.end_time = 'TBD';
        } else if (event.labels.end_time === '12:00 pm' || event.labels.end_time === '12:00') {
          event.labels.end_time = 'Noon';
        } else if (event.labels.end_time === '12:00 am' || event.labels.end_time === '00:00') {
          event.labels.end_time = 'Midnight';
        }
        event.labels.time_duration = `${event.labels.start_time}  -  ${event.labels.end_time}`;
      } else {
        event.labels.time_duration = event.labels.start_time;
      }
      event.all_day = false;
    }

    if (event.labels.YYYMMDD !== event.labels.end_moment.format('YYYY-MM-DD')) {
      event.overnight = true;
    }

    if (event.locations.length) {
      event.labels.location = event.locations[0].name;

      if (event.locations.length > 1) {
        event.labels.location = event.locations.reduce(
          (reduced, location) => `${reduced}, ${location.name}`, event.labels.locations
        );
      }
    }

    if(event.virtual_meeting_link) {
      event.virtual_meeting_link = unescape(event.virtual_meeting_link);
    }

    event.isLoading = false;
    event._isProcessed = true;

    return event;
  }

  static formatSearchBrowse(section, subsections, title, selectAllOption) {
    let formatted = {};
    formatted.section = section;

    formatted.subsections = subsections.split(',');
    formatted.subsections_hidden = null;
    formatted.subsections_search = null;

    formatted.hidden_ids = `hidden_${section}`;
    formatted.title = title;
    // formatted.title_singular = EditEventDetails.makeSingular(title);

    if (formatted.subsections.length > 1) {
      formatted.use_id_prefixes = true;
    } else {
      formatted.use_id_prefixes = false;
    }

    formatted.single_selection = false;
    formatted.subsections_single_selection = null;

    formatted.additional_fields = '';

    formatted.select_all_option = selectAllOption;

    formatted.hierarchy = null;
    formatted.subsections_hierarchy = null;

    formatted.sub_object = null;
    formatted.subsections_sub_object = null;

    formatted.subsections_disabled = null;
    formatted.subsections_disabled_always_show = null;

    formatted.subsection_primary = null;

    formatted.use_meta_filters = false;

    return formatted;
  }

  static getMainSubsections(data) {
    let subsections = [];

    each(data.subsections, (subsection) => {
      if (!data.subsections_hidden || data.subsections_hidden.indexOf(subsection) === -1) {
        subsections.push(subsection);
      }
    });

    return subsections;
  }

  static getSubsectionTitles(subsection) { // , singular
    let title = '';
    const singular = false;

    if (subsection === 'school') {
      title = 'Schools';
    } else if (subsection === 'school_restricted') {
      title = 'Schools';
    } else if (subsection === 'filtering_school') {
      title = 'Schools';
    } else if (subsection === 'district_school') {
      title = 'District Schools';
    } else if (subsection === 'district_school_restricted') {
      title = 'District Schools';
    } else if (subsection === 'directory_school') {
      title = 'Directory Schools';
    } else if (subsection === 'filtering_district_school') {
      title = 'District Schools';
    } else if (subsection === 'tandem_school') {
      title = 'Tandem Schools';
    } else if (subsection === 'filtering_tandem_school') {
      title = 'Tandem Schools';
    } else if (subsection === 'non_district_school') {
      title = 'Other Schools';
    } else if (subsection === 'non_district_school_restricted') {
      title = 'Other Schools';
    } else if (subsection === 'school_group_school' || subsection === 'school_group_school_restricted') {
      if (singular) {
        title = 'School or Group';
      } else {
        title = 'Schools & Groups';
      }
    } else if (subsection === 'community_group_school' || subsection === 'community_group_school_restricted') {
      if (singular) {
        title = 'Other School or Group';
      } else {
        title = 'Other Schools & Groups';
      }
    } else if (subsection === 'local_facility') {
      title = 'Facilities';
    } else if (subsection === 'filtering_local_facility') {
      title = 'Facilities';
    } else if (subsection === 'facility') {
      title = 'Facilities';
    } else if (subsection === 'school_local_facility') {
      if (singular) {
        title = 'School or Facility';
      } else {
        title = 'Schools & Facilities';
      }
    } else if (subsection === 'remote_facility') {
      title = 'Other Locations';
    } else if (subsection === 'filtering_remote_facility') {
      title = 'Other Locations';
    } else if (subsection === 'place') {
      title = 'Other Locations';
    } else if (subsection === 'school_remote_facility') {
      if (singular) {
        title = 'Other School or Location';
      } else {
        title = 'Other Schools & Locations';
      }
    } else if (subsection === 'user' || subsection === 'school_user') {
      title = 'People';
    } else if (subsection === 'admin_user') {
      title = 'Administrators';
    } else if (subsection === 'private_user') {
      title = 'Private Viewers';
    } else if (subsection === 'contact') {
      title = 'Contacts';
    } else if (subsection === 'event_type') {
      title = 'Event Types';
    } else if (subsection === 'filtering_event_type') {
      title = 'Event Types';
    } else if (subsection === 'activity') {
      title = 'Activities';
    } else if (subsection === 'filtering_activity') {
      title = 'Activities';
    } else if (subsection === 'department') {
      title = 'Departments';
    } else if (subsection === 'filtering_department') {
      title = 'Departments';
    } else if (subsection === 'group') {
      title = 'Groups';
    } else if (subsection === 'group_restricted') {
      title = 'Groups';
    } else if (subsection === 'group_school_restricted') {
      title = 'Groups';
    } else if (subsection === 'filtering_group') {
      title = 'Groups';
    } else if (subsection === 'school_group') {
      title = 'Groups';
    } else if (subsection === 'school_group_restricted') {
      title = 'Groups';
    } else if (subsection === 'community_group') {
      title = 'Other Groups';
    } else if (subsection === 'community_group_restricted') {
      title = 'Other Groups';
    } else if (subsection === 'my_community_group') {
      title = 'My Groups';
    } else if (subsection === 'group_type') {
      title = 'Group Types';
    } else if (subsection === 'group_sub_type') {
      title = 'Group Sub-Types';
    } else if (subsection === 'community_group_sub_type') {
      title = 'Group Sub-Types';
    } else if (subsection === 'equipment') {
      title = 'Equipment';
    } else if (subsection === 'school_equipment') {
      title = 'Equipment';
    } else if (subsection === 'driver') {
      title = 'Drivers';
    } else if (subsection === 'bus') {
      title = 'Buses';
    } else if (subsection === 'charter') {
      title = 'Charters';
    } else if (subsection === 'primary_group') {
      title = 'Primary Groups';
    } else if (subsection === 'user_primary_group') {
      title = 'Your Primary Groups';
    } else if (subsection === 'permission_group') {
      title = 'Permission Groups';
    } else if (subsection === 'odometer_rate') {
      title = 'Odometer Rates';
    } else if (subsection === 'minimum_trip_hour') {
      title = 'Minimum Trip Hours';
    } else if (subsection === 'vehicle') {
      title = 'Vehicles';
    } else if (subsection === 'vehicle_type') {
      title = 'Vehicle Types';
    } else if (subsection === 'budget') {
      title = 'Budgets';
    } else if (subsection === 'bill_to') {
      title = 'Bill Tos';
    } else if (subsection === 'pay_rate') {
      title = 'Pay Rates';
    } else if (subsection === 'report') {
      title = 'Reports';
    } else if (subsection === 'trans_report') {
      title = 'Transportation Reports';
    } else if (subsection === 'menu_type') {
      title = 'Menu Types';
    } else if (subsection === 'event_template') {
      title = 'Event Templates';
    } else if (subsection === 'report_template') {
      title = 'Schedule Templates';
    } else if (subsection === 'color_profile') {
      title = 'Color Profiles';
    } else if (subsection === 'merchant_account') {
      title = 'Merchant Accounts';
    } else if (subsection === 'line_item') {
      title = 'Line Items';
    } else if (subsection === 'form') {
      title = 'Forms';
    } else if (subsection === 'address') {
      title = 'Addresses';
    } else if (subsection === 'my_address') {
      title = 'My Addresses';
    } else if (subsection === 'user_role') {
      title = 'User Roles';
    } else if (subsection === 'facility_type') {
      title = 'Facility Types';
    } else if (subsection === 'local_facility_rentable') {
      title = 'Facilities';
    } else if (subsection === 'available_time') {
      title = 'Available Times';
    } else if (subsection === 'approval_step') {
      title = 'Approval Step';
    } else if (subsection === 'approval_role') {
      title = 'Approval Role';
    } else if (subsection === 'approval_decision') {
      title = 'Approval Step';
    } else if (subsection === 'approval_rule') {
      title = 'Approval Rule';
    } else if (subsection === 'time_period_set') {
      title = 'Time Period Set';
    } else if (subsection === 'google_place') {
      title = 'Google Place';
    } else if (subsection === 'broadcaster') {
      title = 'Broadcasters';
    } else if (subsection === 'organization') {
      title = 'Organizations';
    } else if (subsection === 'notification_group') {
      title = 'Notification Groups';
    } else if (subsection === 'mobile_provider') {
      title = 'Mobile Providers';
    }

    // if(singular) {
    //   title = makeSingular(title);
    // }

    return title;
  }

  static getDayOfWeekStart() {

    if (has(window.LOGIN, 'prefs.day_of_week_start')) {
      if (window.LOGIN.prefs.day_of_week_start) {
        return parseInt(window.LOGIN.prefs.day_of_week_start);
      }
    }

    if (has(window.CONFIG, 'day_of_week_start')) {
      if (window.CONFIG.day_of_week_start) {
        return parseInt(window.CONFIG.day_of_week_start);
      }
    }

    return 1;
  }

  static getDateFormatCode() {

    if (has(window.LOGIN, 'prefs.date_format')) {
      if (window.LOGIN.prefs.date_format && window.LOGIN.prefs.date_format.length > 0) {
        return window.LOGIN.prefs.date_format;
      }
    }

    if (has(window.CONFIG, 'date_format')) {
      if (window.CONFIG.date_format && window.CONFIG.date_format.length > 0) {
        return window.CONFIG.date_format;
      }
    }

    return '1';
  }

  static getTimeFormatCode() {

    if (has(window.LOGIN, 'prefs.time_format')) {
      if (window.LOGIN.prefs.time_format && window.LOGIN.prefs.time_format.length > 0) {
        return window.LOGIN.prefs.time_format;
      }
    }
    
    if (has(window.CONFIG, 'time_format')) {
      if (window.CONFIG.time_format && window.CONFIG.time_format.length > 0) {
        return window.CONFIG.time_format;
      }
    }

    return '1';
  }

  static parseTime(time_string) {

    var time_string_lower = time_string.toLowerCase();
    
    var ampm_index = time_string_lower.indexOf("am");
    if(ampm_index == -1) ampm_index = time_string_lower.indexOf("pm");
    
    if(ampm_index != -1) {
      if(time_string.charAt(ampm_index-1) == ' ') {
        time_string = time_string.substr(0, ampm_index-1) + " " + time_string.substring(ampm_index);
      }
      else {   
        time_string = time_string.substr(0, ampm_index) + " " + time_string.substr(ampm_index);
      }
    }
    
    return time_string;
  }

  static getDateObj(date_string, format) {
    if(typeof format == 'undefined') {
      var format = Calendar.getDateFormat(date_string);
    }

    return Date.parseDate(date_string, format);
  }

  static getDateFormat(date_string) {
    if(typeof date_string != 'undefined') {
      if(date_string.indexOf('-') != -1) return '%Y-%m-%d';
    }

    const dateFormatCode = Calendar.getDateFormatCode();

    if(dateFormatCode == '2') {
      return '%d/%m/%Y';
    } else {
      return '%m/%d/%Y';
    }
  }

  static getDateMomentFormat(date_string) {
    if(typeof date_string != 'undefined') {
      if(date_string.indexOf('-') != -1) return 'YYYY-MM-DD';
    }

    const dateFormatCode = Calendar.getDateFormatCode();

    if(dateFormatCode == '2') {
      return 'DD/MM/YYYY';
    } else {
      return 'MM/DD/YYYY';
    }
  }

  static parseDate(date_string, format) {

    var date_obj = Calendar.getDateObj(date_string, format);

    const dateFormatCode = Calendar.getDateFormatCode();

    if(dateFormatCode == '2') return date_obj.getDate() + '/' + (date_obj.getMonth() + 1) + '/' + date_obj.getFullYear();
    else return (date_obj.getMonth() + 1) + '/' + date_obj.getDate() + '/' + date_obj.getFullYear();
  }

  static getTimeFormat(time_string) {
    if(typeof time_string != 'undefined') {
      if(time_string.indexOf('am') != -1 || time_string.indexOf('pm') != -1) return '%l:%M %p';
    }

    const timeFormatCode = Calendar.getTimeFormatCode();

    if(timeFormatCode == '2') {
      return '%k:%M';
    } else {
      return '%l:%M %p';
    }
  }

  static getTimeMomentFormat(time_string) {
    if(typeof time_string != 'undefined') {
      if(time_string.indexOf('am') != -1 || time_string.indexOf('pm') != -1) return 'h:mm a';
    }

    const timeFormatCode = Calendar.getTimeFormatCode();

    if(timeFormatCode == '2') {
      return 'H:mm';
    } else {
      return 'h:mm a';
    }
  }

  static getTimeDateObj(i_time, i_date) {
    if(typeof i_date == 'undefined') var i_date = '1975-01-01';
    
    const date_format = Calendar.getDateFormat(i_date);

    i_time = Calendar.parseTime(i_time);

    const time_format = Calendar.getTimeFormat(i_time);
    
    return Date.parseDate(i_date + " " + i_time, date_format + " " + time_format);
  }

  static getTimeTimestamp(i_time, i_date) {

    if(typeof i_date == 'undefined') var i_date = '1975-01-01';
    
    var time_obj = Calendar.getTimeDateObj(i_time, i_date);
    
    return time_obj.getTime();
  }

  static getTimeString(hours, minutes) {

    const timeFormatCode = Calendar.getTimeFormatCode();

    if(timeFormatCode == '2') {
      if(minutes < 10) minutes = "0" + minutes;

      return hours + ":" + minutes;
    } else {
      if(hours > 11) {
        if(hours > 12) hours = hours - 12;
        var ampm = "pm";
      }
      else var ampm = "am";
      
      if(hours == 0) hours = 12;
      
      if(minutes < 10) minutes = "0" + minutes;
      
      return hours + ":" + minutes + " " + ampm;
    }
  }

  static getDateTimestamp(i_date)
  {
    var date_obj = Calendar.getDateObj(i_date);

    return date_obj.getTime();
  }

  static getDateTimeTimestamp(i_date_time)
  {
    var time_obj = new Date();
    time_obj.setTime(Date.parse(i_date_time));
    
    return time_obj.getTime();
  }

  static getMinutesFromTime(from_date, from_time, to_date, to_time)
  {
    from_date = Calendar.parseDate(from_date);
    from_time = Calendar.parseTime(from_time);

    to_date = Calendar.parseDate(to_date);
    to_time = Calendar.parseTime(to_time);
    
    var from_time_obj = Calendar.getTimeDateObj(from_time, from_date);

    var to_time_obj = Calendar.getTimeDateObj(to_time, to_date);

    return ((to_time_obj.getTime() - from_time_obj.getTime()) / 60000);
  }

  static getHourMinuteFromMinutes(minutes)
  {
    this.hour = Math.floor(minutes / 60);
    this.minute = minutes % 60;
  }

  static dayMumSuffix(day_num)
  {
    day_num %= 100;

    var day_num_suffix = 'th';

    if(day_num < 4 || day_num > 20) {
      day_num = day_num.toString();

      const last_num = parseInt(day_num[ day_num.length - 1 ]);

      if(last_num == 1) {
        day_num_suffix = 'st';
      }
      else if(last_num == 2) {
        day_num_suffix = 'nd';
      }
      else if(last_num == 3) {
        day_num_suffix = 'rd';
      }
    }

    return day_num_suffix;
  }

  static dateFormat(format, timestamp)
  {
    var newDate = new Date();
    newDate.setTime(timestamp);

    return newDate.print(format);
  }

  static getStartOfDayTime()
  {
    const timeFormatCode = Calendar.getTimeFormatCode();

    if(timeFormatCode == '2') return '00:00';
    else return '12:00 am';
  }

  static getEndOfDayTime()
  {
    const timeFormatCode = Calendar.getTimeFormatCode();

    if(timeFormatCode == '2') return '23:59';
    else return '11:59 pm';
  }

  static eventTimeErrorCheck(error_msg, time1, time2, date1, date2, time1_text, time2_text)
  {
    if(time1.length == 0 && time2.length != 0) error_msg += "Please enter a " + time1_text + "<br />";
    if(time1.length != 0 && time2.length == 0) error_msg += "Please enter a " + time2_text + "<br />";
    
    if(time1.length != 0 && time2.length != 0 && (date2.length == 0 || date2 == date1)) {

      time1 = Calendar.getTimeTimestamp(time1);
      time2 = Calendar.getTimeTimestamp(time2);
      
      if(isNaN(time1)) error_msg += "Enter a valid " + time1_text + "<br />";
      if(isNaN(time2)) error_msg += "Enter a valid " + time2_text + "<br />";
      
      if((date2.length == 0 || date2 == date1) && time2 <= time1 && !isNaN(time1) && !isNaN(time2)) {

        error_msg += "The " + time1_text + " must be before the " + time2_text + "<br />";
      }
    }
    
    return error_msg;
  }

  // full month names
  static _MN = new Array
  ("January",
   "February",
   "March",
   "April",
   "May",
   "June",
   "July",
   "August",
   "September",
   "October",
   "November",
   "December");

  // full day names
  static _DN = new Array
  ("Sunday",
   "Monday",
   "Tuesday",
   "Wednesday",
   "Thursday",
   "Friday",
   "Saturday",
   "Sunday");

  // short day names
  static _SDN = new Array
  ("Sun",
   "Mon",
   "Tue",
   "Wed",
   "Thu",
   "Fri",
   "Sat",
   "Sun");

  // short month names
  static _SMN = new Array
  ("Jan",
   "Feb",
   "Mar",
   "Apr",
   "May",
   "Jun",
   "Jul",
   "Aug",
   "Sep",
   "Oct",
   "Nov",
   "Dec");

}

// BEGIN: DATE OBJECT PATCHES

/** Adds the number of days array to the Date object. */
Date._MD = new Array(31,28,31,30,31,30,31,31,30,31,30,31);

/** Constants used for time computations */
Date.SECOND = 1000 /* milliseconds */;
Date.MINUTE = 60 * Date.SECOND;
Date.HOUR   = 60 * Date.MINUTE;
Date.DAY    = 24 * Date.HOUR;
Date.WEEK   =  7 * Date.DAY;

Date.parseDate = function(str, fmt) {
  var today = new Date();
  var y = 0;
  var m = -1;
  var d = 0;
  var a = str.split(/\W+/);
  var b = fmt.match(/%./g);
  var i = 0, j = 0;
  var hr = 0;
  var min = 0;
  for (i = 0; i < a.length; ++i) {
    if (!a[i])
      continue;
    switch (b[i]) {
        case "%d":
        case "%e":
      d = parseInt(a[i], 10);
      break;

        case "%m":
      m = parseInt(a[i], 10) - 1;
      break;

        case "%Y":
        case "%y":
      y = parseInt(a[i], 10);
      (y < 100) && (y += (y > 29) ? 1900 : 2000);
      break;

        case "%b":
        case "%B":
      for (j = 0; j < 12; ++j) {
        if (Calendar._MN[j].substr(0, a[i].length).toLowerCase() == a[i].toLowerCase()) { m = j; break; }
      }
      break;

        case "%H":
        case "%I":
        case "%k":
        case "%l":
      hr = parseInt(a[i], 10);
      break;

        case "%P":
        case "%p":
      if (/pm/i.test(a[i]) && hr < 12)
        hr += 12;
      else if (/am/i.test(a[i]) && hr >= 12)
        hr -= 12;
      break;

        case "%M":
      min = parseInt(a[i], 10);
      break;
    }
  }
  if (isNaN(y)) y = today.getFullYear();
  if (isNaN(m)) m = today.getMonth();
  if (isNaN(d)) d = today.getDate();
  if (isNaN(hr)) hr = today.getHours();
  if (isNaN(min)) min = today.getMinutes();
  if (y != 0 && m != -1 && d != 0)
    return new Date(y, m, d, hr, min, 0);
  y = 0; m = -1; d = 0;
  for (i = 0; i < a.length; ++i) {
    if (a[i].search(/[a-zA-Z]+/) != -1) {
      var t = -1;
      for (j = 0; j < 12; ++j) {
        if (Calendar._MN[j].substr(0, a[i].length).toLowerCase() == a[i].toLowerCase()) { t = j; break; }
      }
      if (t != -1) {
        if (m != -1) {
          d = m+1;
        }
        m = t;
      }
    } else if (parseInt(a[i], 10) <= 12 && m == -1) {
      m = a[i]-1;
    } else if (parseInt(a[i], 10) > 31 && y == 0) {
      y = parseInt(a[i], 10);
      (y < 100) && (y += (y > 29) ? 1900 : 2000);
    } else if (d == 0) {
      d = a[i];
    }
  }
  if (y == 0)
    y = today.getFullYear();
  if (m != -1 && d != 0)
    return new Date(y, m, d, hr, min, 0);
  return today;
};

/** Returns the number of days in the current month */
Date.prototype.getMonthDays = function(month) {
  var year = this.getFullYear();
  if (typeof month == "undefined") {
    month = this.getMonth();
  }
  if (((0 == (year%4)) && ( (0 != (year%100)) || (0 == (year%400)))) && month == 1) {
    return 29;
  } else {
    return Date._MD[month];
  }
};

/** Returns the number of day in the year. */
Date.prototype.getDayOfYear = function() {
  var now = new Date(this.getFullYear(), this.getMonth(), this.getDate(), 0, 0, 0);
  var then = new Date(this.getFullYear(), 0, 0, 0, 0, 0);
  var time = now - then;
  return Math.floor(time / Date.DAY);
};

/** Returns the number of the week in year, as defined in ISO 8601. */
Date.prototype.getWeekNumber = function() {
  var d = new Date(this.getFullYear(), this.getMonth(), this.getDate(), 0, 0, 0);
  var DoW = d.getDay();
  d.setDate(d.getDate() - (DoW + 6) % 7 + 3); // Nearest Thu
  var ms = d.valueOf(); // GMT
  d.setMonth(0);
  d.setDate(4); // Thu in Week 1
  return Math.round((ms - d.valueOf()) / (7 * 864e5)) + 1;
};

/** Checks date and time equality */
Date.prototype.equalsTo = function(date) {
  return ((this.getFullYear() == date.getFullYear()) &&
    (this.getMonth() == date.getMonth()) &&
    (this.getDate() == date.getDate()) &&
    (this.getHours() == date.getHours()) &&
    (this.getMinutes() == date.getMinutes()));
};

Date.prototype.dayNumSuffix = function () {
  var day_num = this.getDate();
  day_num %= 100;

  var day_num_suffix = 'th';

  if(day_num < 4 || day_num > 20) {
    day_num = day_num.toString();

    var last_num = parseInt(day_num[ day_num.length - 1 ]);

    if(last_num == 1) {
      day_num_suffix = 'st';
    }
    else if(last_num == 2) {
      day_num_suffix = 'nd';
    }
    else if(last_num == 3) {
      day_num_suffix = 'rd';
    }
  }

  return day_num_suffix;
};

/** Prints the date in a string according to the given format. */
Date.prototype.print = function (str) {
  var m = this.getMonth();
  var d = this.getDate();
  var y = this.getFullYear();
  var wn = this.getWeekNumber();
  var w = this.getDay();
  var s = {};
  var hr = this.getHours();
  var pm = (hr >= 12);
  var ir = (pm) ? (hr - 12) : hr;
  var dy = this.getDayOfYear();
  if (ir == 0)
    ir = 12;
  var min = this.getMinutes();
  var sec = this.getSeconds();
  s["%a"] = Calendar._SDN[w]; // abbreviated weekday name [FIXME: I18N]
  s["%A"] = Calendar._DN[w]; // full weekday name
  s["%b"] = Calendar._SMN[m]; // abbreviated month name [FIXME: I18N]
  s["%B"] = Calendar._MN[m]; // full month name
  // FIXME: %c : preferred date and time representation for the current locale
  s["%C"] = 1 + Math.floor(y / 100); // the century number
  s["%d"] = (d < 10) ? ("0" + d) : d; // the day of the month (range 01 to 31)
  s["%e"] = d; // the day of the month (range 1 to 31)
  // FIXME: %D : american date style: %m/%d/%y
  // FIXME: %E, %F, %G, %g, %h (man strftime)
  s["%H"] = (hr < 10) ? ("0" + hr) : hr; // hour, range 00 to 23 (24h format)
  s["%I"] = (ir < 10) ? ("0" + ir) : ir; // hour, range 01 to 12 (12h format)
  s["%j"] = (dy < 100) ? ((dy < 10) ? ("00" + dy) : ("0" + dy)) : dy; // day of the year (range 001 to 366)
  s["%k"] = hr;   // hour, range 0 to 23 (24h format)
  s["%l"] = ir;   // hour, range 1 to 12 (12h format)
  s["%m"] = (m < 9) ? ("0" + (1+m)) : (1+m); // month, range 01 to 12
  s["%M"] = (min < 10) ? ("0" + min) : min; // minute, range 00 to 59
  s["%n"] = "\n";   // a newline character
  s["%o"] = this.dayNumSuffix();  //English ordinal suffix for the day of the month, 2 characters (st, nd, rd, or th)
  s["%p"] = pm ? "PM" : "AM";
  s["%P"] = pm ? "pm" : "am";
  // FIXME: %r : the time in am/pm notation %I:%M:%S %p
  // FIXME: %R : the time in 24-hour notation %H:%M
  s["%s"] = Math.floor(this.getTime() / 1000);
  s["%S"] = (sec < 10) ? ("0" + sec) : sec; // seconds, range 00 to 59
  s["%t"] = "\t";   // a tab character
  // FIXME: %T : the time in 24-hour notation (%H:%M:%S)
  s["%U"] = s["%W"] = s["%V"] = (wn < 10) ? ("0" + wn) : wn;
  s["%u"] = w + 1;  // the day of the week (range 1 to 7, 1 = MON)
  s["%w"] = w;    // the day of the week (range 0 to 6, 0 = SUN)
  // FIXME: %x : preferred date representation for the current locale without the time
  // FIXME: %X : preferred time representation for the current locale without the date
  s["%y"] = ('' + y).substr(2, 2); // year without the century (range 00 to 99)
  s["%Y"] = y;    // year with the century
  s["%%"] = "%";    // a literal '%' character

  var re = /%./g;
  
  return str.replace(re, function (par) { return s[par] || par; });
};

var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

Date.prototype.getMonthName = function() {
  return months[ this.getMonth() ];
};



// WEBPACK FOOTER //
// ./src/app/lib/Calendar.js